import { MappedAppPages } from '@hellodarwin/core/lib/features/entities/pages-entities';
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

const ClientAssistantPage = lazy(() => import('./assistant-page'));
const ClientAiIntegrationPage = lazy(() => import('./ai-integration'));
const BillingRedirect = lazy(() => import('./billing-redirect'));
const ClientContactPage = lazy(() => import('./contact'));
const ExpertDashboardPage = lazy(() => import('./expert-dashboard'));
const ClientFAQPage = lazy(() => import('./faq'));
const ClientFundingExplorer = lazy(
  () => import('./funding-explorer/client-funding-explorer-page'),
);
const ClientFundingExplorerSingleGrantPage = lazy(
  () =>
    import(
      './funding-explorer/single-grant/client-funding-explorer-single-grant'
    ),
);
const ClientFundingIntelligencePage = lazy(
  () => import('./funding-intelligence'),
);
const ClientFundingIntelligenceAcceptedApplicationsPage = lazy(
  () => import('./funding-intelligence/accepted-applications'),
);
const ClientGrantProfilesPage = lazy(() => import('./grant-profiles'));
const ClientNotificationsPage = lazy(() => import('./notifications'));
const ClientResourcesPage = lazy(() => import('./resources'));
const ClientArticleSinglePage = lazy(() => import('./resources/[articleId]'));
const ClientSettingsPage = lazy(() => import('./settings'));

const expertPages: MappedAppPages = {
  '/dashboard': {
    pathname: '/dashboard',
    title: 'Dashboard',
    component: <ExpertDashboardPage />,
    requiredAccess: 'expert',
  },
  '/billing': {
    pathname: '/billing',
    title: 'Billing',
    component: <BillingRedirect />,
    requiredAccess: 'expert',
  },
  '/funding-explorer': {
    pathname: '/funding-explorer',
    title: 'Funding Explorer',
    component: <ClientFundingExplorer />,
    requiredAccess: 'expert',
    children: {
      history: {
        pathname: '/funding-explorer/history',
        title: 'Viewed Programs',
        component: <ClientFundingExplorer />,
        requiredAccess: 'expert',
      },
      ':id': {
        pathname: '/funding-explorer/:id',
        title: 'Single Grant',
        component: <ClientFundingExplorerSingleGrantPage />,
        naturalScroll: true,
        requiredAccess: 'expert',
      },
    },
  },
  '/assistant': {
    pathname: '/assistant',
    title: 'Assistant',
    component: <ClientAssistantPage />,
    requiredAccess: 'expert',
    children: {
      ':conversationId': {
        pathname: '/assistant/:conversationId',
        title: 'Assistant',
        component: <ClientAssistantPage />,
        requiredAccess: 'expert',
      },
    },
  },
  '/grant-profiles': {
    pathname: '/grant-profiles',
    title: 'Grant Profiles',
    component: <ClientGrantProfilesPage />,
    requiredAccess: 'expert',
  },
  '/funding-intelligence': {
    pathname: '/funding-intelligence',
    title: 'Funding Intelligence',
    component: <ClientFundingIntelligencePage />,
    requiredAccess: 'expert',
    children: {
      ':grantId': {
        pathname: '/funding-intelligence/:grantId',
        title: 'Accepted Applications',
        component: <ClientFundingIntelligenceAcceptedApplicationsPage />,
        requiredAccess: 'expert',
      },
    },
  },
  '/alerts': {
    pathname: '/alerts',
    title: 'Alerts',
    component: <ClientNotificationsPage />,
    requiredAccess: 'expert',
  },
  '/notifications': {
    pathname: '/notifications',
    title: 'Notifications',
    component: <Navigate to="/alerts" />,
    requiredAccess: 'expert',
  },
  '/ai-integration': {
    pathname: '/ai-integration',
    title: 'AI Integration',
    component: <ClientAiIntegrationPage />,
    requiredAccess: 'expert',
  },
  '/resources': {
    pathname: '/resources',
    title: 'Resources',
    component: <ClientResourcesPage />,
    requiredAccess: 'expert',
    children: {
      ':articleId': {
        pathname: '/resources/:articleId',
        title: 'Redirect to Resources',
        component: <Navigate to="/resources" />,
        requiredAccess: 'expert',
      },
    },
  },
  '/articles': {
    pathname: '/articles',
    title: 'Articles',
    component: <Navigate to="/resources" />,
    requiredAccess: 'expert',
    children: {
      ':articleId': {
        pathname: '/articles/:articleId',
        title: 'Single Article',
        component: <ClientArticleSinglePage />,
        requiredAccess: 'expert',
      },
    },
  },
  '/faq': {
    pathname: '/faq',
    title: 'FAQ',
    component: <ClientFAQPage />,
    requiredAccess: 'expert',
  },
  '/contact-us': {
    pathname: '/contact-us',
    title: 'Contact',
    component: <ClientContactPage />,
    requiredAccess: 'expert',
  },
  '/profile': {
    pathname: '/profile',
    title: 'Profile',
    component: <Navigate to="/profile/contact" />,
    requiredAccess: 'expert',
    children: {
      contact: {
        pathname: '/profile/contact',
        title: 'Contact',
        component: <ClientSettingsPage defaultActiveKey="contact" />,
        requiredAccess: 'expert',
      },
      company: {
        pathname: '/profile/company',
        title: 'Company',
        component: <ClientSettingsPage defaultActiveKey="company" />,
        requiredAccess: 'expert',
      },
      users: {
        pathname: '/profile/users',
        title: 'Users',
        component: <ClientSettingsPage defaultActiveKey="users" />,
        requiredAccess: 'expert',
      },
      alerts: {
        pathname: '/profile/alerts',
        title: 'Alert Preferences',
        component: <ClientSettingsPage defaultActiveKey="alerts" />,
        requiredAccess: 'expert',
      },
    },
  },
};

export default expertPages;
